import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { Shuffle } from 'lucide-react'
import { faker } from '@faker-js/faker'
import { useAppAuth } from '@/contexts/app-auth.context'

const E164_REGEX = /^\+[1-9]\d{6,14}(#\d+)?$/

const schema = z.object({
  lead_uid: z.string().min(1, 'Lead UID is required'),
  listing_uid: z.string().min(1, 'Listing is required'),
  address: z.string().min(1, 'Address is required'),
  name: z.string().optional(),
  email: z.union([z.literal(''), z.string().email('Invalid email')]).optional(),
  phone: z
    .string()
    .min(1, 'Phone is required')
    .regex(
      E164_REGEX,
      'Must be E.164 format (e.g. +12065551234 or +12065551234#2)',
    ),
  state: z.string().optional(),
  move_in_date: z.string().optional(),
  message: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

interface Listing {
  id: string
  externalId?: string
  address: string
  unit?: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const

function randomizeLead(): Partial<FormValues> {
  const name = faker.person.fullName()
  const messages = [
    "Hi, I saw your listing online and I'm very interested. Could we schedule a showing?",
    "I'm looking for a place starting next month. Is this unit still available?",
    'My family of 3 is relocating and this looks perfect. Please reach out!',
    'I have a small dog — is the unit pet friendly? Would love to see it.',
    'I submitted an application online. Looking forward to hearing from you.',
  ]
  const daysOut = faker.number.int({ min: 30, max: 90 })
  const moveIn = new Date()
  moveIn.setDate(moveIn.getDate() + daysOut)

  return {
    lead_uid: `sm-sim-${faker.string.alphanumeric(10)}`,
    name,
    email: faker.internet
      .email({ firstName: name.split(' ')[0] })
      .toLowerCase(),
    state: faker.helpers.arrayElement(US_STATES),
    move_in_date: moveIn.toISOString().slice(0, 10),
    message: faker.helpers.arrayElement(messages),
  }
}

export function SimulateShowMojoLeadModal({ open, onOpenChange }: Props) {
  const { currentCompany } = useAppAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [listings, setListings] = useState<Listing[]>([])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      lead_uid: '',
      listing_uid: '',
      address: '',
      name: '',
      email: '',
      phone: '',
      state: '',
      move_in_date: '',
      message: '',
    },
  })

  useEffect(() => {
    if (!open || !currentCompany?.id) return
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
    fetch(`${apiUrl}/api/admin/listings?companyId=${currentCompany.id}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(res => {
        const arr = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : []
        setListings(arr)
      })
      .catch(() => setListings([]))
  }, [open, currentCompany?.id])

  const handleListingSelect = (listingId: string) => {
    const listing = listings.find(l => l.externalId === listingId)
    if (!listing) return
    form.setValue('listing_uid', listing.externalId ?? '', {
      shouldValidate: true,
    })
    const address = listing.unit
      ? `${listing.address} Unit ${listing.unit}`
      : listing.address
    form.setValue('address', address, { shouldValidate: true })
  }

  const handleRandomize = () => {
    const values = randomizeLead()
    Object.entries(values).forEach(([key, value]) => {
      form.setValue(key as keyof FormValues, value as string, {
        shouldValidate: true,
      })
    })
  }

  const onSubmit: SubmitHandler<FormValues> = async data => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    setIsSubmitting(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
      const res = await fetch(
        `${apiUrl}/api/admin/integrations/showmojo/simulate-lead/${currentCompany.id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_uid: data.lead_uid,
            listing_uid: data.listing_uid,
            address: data.address,
            name: data.name || undefined,
            email: data.email || undefined,
            phone: data.phone || undefined,
            state: data.state || undefined,
            move_in_date: data.move_in_date || undefined,
            message: data.message || undefined,
          }),
        },
      )

      const body = await res.json()

      if (!res.ok) {
        throw new Error(body?.message ?? `Request failed: ${res.status}`)
      }

      const accepted = body?.data?.accepted ?? body?.accepted
      const externalId = body?.data?.externalId ?? body?.externalId
      if (accepted === true) {
        toast.success(
          externalId
            ? `Lead accepted — sequence created (externalId: ${externalId})`
            : 'Lead accepted — sequence created',
        )
        onOpenChange(false)
      } else {
        toast.warning('Lead rejected by webhook (accepted: false)')
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to simulate lead',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Simulate ShowMojo: Webhook Lead</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <FormField
              control={form.control}
              name="listing_uid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Listing</FormLabel>
                  <Select
                    onValueChange={val => {
                      field.onChange(val)
                      handleListingSelect(val)
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            listings.length === 0
                              ? 'No listings found'
                              : 'Select a listing…'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {listings
                        .filter(l => l.externalId)
                        .map(l => (
                          <SelectItem key={l.id} value={l.externalId!}>
                            {l.address}
                            {l.unit ? ` · ${l.unit}` : ''}
                            <span className="ml-2 text-xs text-muted-foreground">
                              {l.externalId}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="123 Main St, Austin, TX 78701"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lead_uid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead UID</FormLabel>
                  <FormControl>
                    <Input placeholder="sm-lead-abc123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane.smith@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+12065551234 or +12065551234#2"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input placeholder="CA" maxLength={2} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="move_in_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Move-in date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="I'm interested in scheduling a showing..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex-row items-center justify-between sm:justify-between">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleRandomize}
                disabled={isSubmitting}
              >
                <Shuffle className="h-3.5 w-3.5" />
                Randomize
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Webhook'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
